// #1115B S7 — Reusable GitHub repo setup section.
//
// Mounts in two contexts (matching §4.1 N2):
//   1. NewProjectModal — modal mode (`projectId` undefined). The section
//      collects the user's mode choice + any per-mode fields and emits
//      changes via onChange. The parent modal includes the collected
//      payload in its POST /projects body as `github: { mode, ... }`.
//      No own submit button.
//
//   2. ProjectGithubSettingsPage — settings mode (`projectId` set). The
//      section owns its submit, calls POST /projects/:id/github-repo
//      directly via apiFetch, and invokes onComplete with the BindResult.
//      Used from the dashboard banner's [Configure GitHub →] CTA.
//
// Mode contract mirrors §3.D5 of the implementation prompt exactly. The
// HTTP-mapping for failure surfaces also lives there.
//
// Styling: matches NewProjectModal's design-token pattern (inline styles +
// CSS vars, no external component lib) per [[feedback_match_neighbour_conventions]].

import { useState } from 'react';
import { apiFetch } from '../lib/api';

export type GitHubSetupMode = 'create_new' | 'link_existing' | 'skip';

export interface GitHubSetupPayload {
  mode:      GitHubSetupMode;
  repoName?: string;
  orgOrUser?: string;
  owner?:    string;
  repo?:     string;
}

/**
 * #3037 AC3 — true when Link-existing is picked but Owner or Repository is
 * empty after trimming. Same rule as BrownfieldOnboardingPage's
 * githubLinkIncomplete; the server stays authoritative for non-UI clients.
 */
export function isLinkExistingIncomplete(payload: GitHubSetupPayload | null): boolean {
  return payload?.mode === 'link_existing' && (!payload.owner?.trim() || !payload.repo?.trim());
}

interface BindMetadata {
  url:           string;
  fullName:      string;
  ownerLogin:    string;
  defaultBranch: string;
}

export interface GitHubSetupResult {
  state:        'PROVISIONED' | 'SKIPPED' | 'ERROR';
  metadata?:    BindMetadata;
  error?:       string;
  nextAction?:  string;
  httpStatus?:  number;
}

interface Props {
  /**
   * When set, the section operates in "settings mode" — submitting POSTs
   * to /projects/:projectId/github-repo immediately. When undefined, the
   * section is in "modal mode" — emits payload via onChange only.
   */
  projectId?: string;
  /**
   * Modal mode: invoked whenever the user changes mode or fills a field.
   * Receives the full payload (or null if no mode picked yet).
   */
  onChange?: (payload: GitHubSetupPayload | null) => void;
  /**
   * Settings mode: invoked after a successful POST. Receives the API
   * response shape so the caller can refresh dashboard state.
   */
  onComplete?: (result: GitHubSetupResult) => void;
  /**
   * #1153 D5 — Modal mode: controlled error message from the parent.
   * When provided, takes precedence over the section's internal errorMsg
   * (set by settings-mode submit failures). Parent owns the lifecycle.
   */
  errorMsg?: string | null;
  /**
   * #1153 D5 — Pairs with `errorMsg`. Drives which action button renders
   * inside the error block: 'reconnect_github' → [Connect GitHub →]
   * (target=_blank to /settings, preserves modal form state per D7);
   * 'check_github_status' → [Retry] (clears error via onRetry).
   */
  errorNextAction?: 'reconnect_github' | 'check_github_status' | null;
  /**
   * #1153 D5 — Fires when the [Retry] button is clicked (only rendered
   * for errorNextAction='check_github_status'). Parent should clear its
   * controlled error state.
   */
  onRetry?: () => void;
  /**
   * #1153 V4 — Fires when the user toggles between create_new /
   * link_existing / skip radio options. Symmetric to onRetry: both
   * clear the parent's controlled error state from different user
   * triggers. Distinct from `onChange` (which also fires for in-mode
   * field edits where the error should persist).
   */
  onModeChange?: () => void;
  /**
   * MODE.B2 (#1175) — uncontrolled-style initial mode. Set on first
   * render only; subsequent changes to this prop are ignored. When
   * provided, the matching radio renders as already-selected.
   *
   * Used by BrownfieldOnboardingPage to pre-select 'link_existing'
   * (the brownfield user is onboarding existing work, so linking an
   * existing repo is the expected default). Parent is responsible for
   * initialising its own onChange-derived payload state to match the
   * same initialMode at mount; the section does NOT emit a payload on
   * mount, by design — keeps NewProjectModal's null-default
   * behaviour byte-identical when this prop is omitted.
   */
  initialMode?: GitHubSetupMode;
  /**
   * #3037 — Modal mode: server validation messages for the Link-existing
   * inputs (from details[] paths `github.owner` / `github.repo`). Each is
   * rendered under its input and wired with aria-describedby/aria-invalid.
   * Absent → the inputs render exactly as before.
   */
  fieldErrors?: { owner?: string; repo?: string };
}

const fieldErrorStyle: React.CSSProperties = {
  marginTop:  4,
  fontFamily: 'var(--font-body)',
  fontSize:   12,
  color:      'var(--error)',
};

const MODES: ReadonlyArray<{ value: GitHubSetupMode; label: string; helper: string }> = [
  {
    value:  'create_new',
    label:  'Create a new repo',
    helper: "BluePrint creates a private GitHub repo using your tenant's configured org. The default name comes from your project name and can be overridden below.",
  },
  {
    value:  'link_existing',
    label:  'Link an existing repo',
    helper: "Bind this project to a GitHub repo you've already created. BluePrint verifies the repo exists and is reachable before binding.",
  },
  {
    value:  'skip',
    label:  'Skip for now',
    helper: "Defer GitHub configuration. The project dashboard will show a reminder banner, and Phase 4 (Development) will be blocked until you configure it.",
  },
] as const;

const inputStyle: React.CSSProperties = {
  display:       'block',
  width:         '100%',
  height:        36,
  background:    'var(--surface-alt)',
  border:        '1px solid var(--border)',
  borderRadius:  'var(--radius-sm)',
  padding:       '0 12px',
  fontFamily:    'var(--font-body)',
  fontSize:      14,
  color:         'var(--text)',
  outline:       'none',
  boxSizing:     'border-box',
};

export default function GitHubRepoSetupSection({
  projectId,
  onChange,
  onComplete,
  errorMsg: controlledErrorMsg,
  errorNextAction,
  onRetry,
  onModeChange,
  initialMode,
  fieldErrors,
}: Props) {
  const isSettingsMode = !!projectId;

  const [mode, setMode]           = useState<GitHubSetupMode | null>(initialMode ?? null);
  const [repoName, setRepoName]   = useState('');
  const [orgOrUser, setOrgOrUser] = useState('');
  const [owner, setOwner]         = useState('');
  const [repo, setRepo]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [internalErrorMsg, setInternalErrorMsg] = useState<string | null>(null);

  // #1153 D5 — controlled prop wins. When the modal owns the error state
  // (controlledErrorMsg !== undefined), it takes precedence over the
  // section's internal state. `undefined` means "uncontrolled" (settings
  // mode behaviour preserved exactly). `null` means "controlled, no error".
  const effectiveErrorMsg = controlledErrorMsg !== undefined ? controlledErrorMsg : internalErrorMsg;

  const buildPayload = (
    m: GitHubSetupMode | null,
    rn = repoName,
    ou = orgOrUser,
    ow = owner,
    rp = repo,
  ): GitHubSetupPayload | null => {
    if (!m) return null;
    if (m === 'create_new')    return { mode: m, ...(rn ? { repoName: rn } : {}), ...(ou ? { orgOrUser: ou } : {}) };
    if (m === 'link_existing') return { mode: m, owner: ow, repo: rp };
    return { mode: m };
  };

  const emitChange = (m: GitHubSetupMode | null, fields?: Partial<{ rn: string; ou: string; ow: string; rp: string }>) => {
    if (onChange) onChange(buildPayload(
      m,
      fields?.rn ?? repoName,
      fields?.ou ?? orgOrUser,
      fields?.ow ?? owner,
      fields?.rp ?? repo,
    ));
  };

  const handleModeChange = (m: GitHubSetupMode) => {
    setMode(m);
    setInternalErrorMsg(null);
    // #1153 V4 — also clear parent-controlled error state. Symmetric to
    // onRetry: both clear the error from different triggers (radio toggle
    // vs explicit Retry click). Distinct from onChange — onChange fires for
    // in-mode field edits where the error must persist.
    if (onModeChange) onModeChange();
    emitChange(m);
  };

  const submittableInSettingsMode = (() => {
    if (!mode) return false;
    if (mode === 'link_existing') return owner.trim().length > 0 && repo.trim().length > 0;
    return true;
  })();

  const handleSubmit = async () => {
    if (!isSettingsMode || !mode || submitting) return;
    setSubmitting(true);
    setInternalErrorMsg(null);
    try {
      const payload = buildPayload(mode);
      const res = await apiFetch<{ state: 'PROVISIONED' | 'SKIPPED'; metadata?: BindMetadata }>(
        `/projects/${projectId}/github-repo`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
      if (onComplete) onComplete({ state: res.state, metadata: res.metadata });
    } catch (err) {
      // apiFetch is expected to throw on non-2xx with the response body's error
      // string. Best-effort surface to the user.
      const msg = err instanceof Error ? err.message : String(err);
      setInternalErrorMsg(msg);
      if (onComplete) onComplete({ state: 'ERROR', error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="github-repo-setup-section"
      style={{
        marginBottom: 16,
        padding:      16,
        border:       '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background:   'var(--surface-alt)',
      }}
    >
      <div
        style={{
          fontFamily:    'var(--font-display)',
          fontSize:      13,
          fontWeight:    600,
          color:         'var(--text)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom:  4,
        }}
      >
        GitHub Repository
      </div>
      <p
        style={{
          fontFamily:   'var(--font-body)',
          fontSize:     11,
          color:        'var(--text-secondary)',
          marginBottom: 12,
          lineHeight:   1.5,
        }}
      >
        BluePrint&apos;s Development phase needs a GitHub repository for branch and PR provisioning. Pick how you&apos;d like to set it up.
      </p>

      <div
        role="radiogroup"
        aria-label="GitHub repository setup mode"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {MODES.map((m) => {
          const checked = mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={checked}
              data-testid={`github-mode-${m.value}`}
              onClick={() => handleModeChange(m.value)}
              style={{
                textAlign:    'left',
                padding:      12,
                borderRadius: 'var(--radius-sm)',
                border:       checked ? '2px solid var(--primary)' : '1px solid var(--border)',
                background:   'var(--surface)',
                cursor:       'pointer',
                outline:      'none',
              }}
            >
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                {m.label}
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                {m.helper}
              </div>
            </button>
          );
        })}
      </div>

      {/* Per-mode field surfaces */}
      {mode === 'create_new' && (
        <div data-testid="create-new-fields" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <label
            htmlFor="github-repo-name"
            style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}
          >
            Repository name <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional — defaults to slugified project name)</span>
          </label>
          <input
            id="github-repo-name"
            data-testid="repo-name-input"
            type="text"
            value={repoName}
            onChange={(e) => { setRepoName(e.target.value); emitChange(mode, { rn: e.target.value }); }}
            placeholder="e.g. blueprint-platform"
            style={inputStyle}
          />
          <label
            htmlFor="github-org-or-user"
            style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginTop: 8, marginBottom: 4 }}
          >
            Organisation or user <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional — defaults to tenant org)</span>
          </label>
          <input
            id="github-org-or-user"
            data-testid="org-or-user-input"
            type="text"
            value={orgOrUser}
            onChange={(e) => { setOrgOrUser(e.target.value); emitChange(mode, { ou: e.target.value }); }}
            placeholder="e.g. acme-corp"
            style={inputStyle}
          />
        </div>
      )}

      {mode === 'link_existing' && (
        <div data-testid="link-existing-fields" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <label
            htmlFor="github-owner"
            style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}
          >
            Owner <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <input
            id="github-owner"
            data-testid="owner-input"
            type="text"
            value={owner}
            onChange={(e) => { setOwner(e.target.value); emitChange(mode, { ow: e.target.value }); }}
            placeholder="acme-corp"
            aria-invalid={fieldErrors?.owner ? true : undefined}
            aria-describedby={fieldErrors?.owner ? 'github-owner-error' : undefined}
            style={inputStyle}
          />
          {fieldErrors?.owner && (
            <div id="github-owner-error" data-testid="github-owner-error" style={fieldErrorStyle}>
              {fieldErrors.owner}
            </div>
          )}
          <label
            htmlFor="github-repo"
            style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginTop: 8, marginBottom: 4 }}
          >
            Repository <span style={{ color: 'var(--error)' }}>*</span>
          </label>
          <input
            id="github-repo"
            data-testid="repo-input"
            type="text"
            value={repo}
            onChange={(e) => { setRepo(e.target.value); emitChange(mode, { rp: e.target.value }); }}
            placeholder="blueprint-platform"
            aria-invalid={fieldErrors?.repo ? true : undefined}
            aria-describedby={fieldErrors?.repo ? 'github-repo-error' : undefined}
            style={inputStyle}
          />
          {fieldErrors?.repo && (
            <div id="github-repo-error" data-testid="github-repo-error" style={fieldErrorStyle}>
              {fieldErrors.repo}
            </div>
          )}
        </div>
      )}

      {effectiveErrorMsg && (
        <div
          role="alert"
          data-testid="github-setup-error"
          style={{
            marginTop:    12,
            padding:      '8px 12px',
            fontFamily:   'var(--font-body)',
            fontSize:     12,
            color:        'var(--error)',
            background:   'rgba(239,68,68,0.06)',
            border:       '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <div>{effectiveErrorMsg}</div>
          {/* #1153 D5 — conditional action button. D7: target='_blank' on the
              Connect GitHub link keeps the modal mounted so React's useState
              preserves form values when the user returns. */}
          {errorNextAction === 'reconnect_github' && (
            <a
              href="/settings"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="github-setup-error-connect-cta"
              style={{
                display:        'inline-block',
                marginTop:      8,
                padding:        '4px 12px',
                fontFamily:     'var(--font-display)',
                fontSize:       12,
                fontWeight:     600,
                color:          'var(--error)',
                background:     'transparent',
                border:         '1px solid var(--error)',
                borderRadius:   12,
                textDecoration: 'none',
              }}
            >
              Connect GitHub →
            </a>
          )}
          {errorNextAction === 'check_github_status' && (
            <button
              type="button"
              onClick={() => onRetry?.()}
              data-testid="github-setup-error-retry-cta"
              style={{
                display:      'inline-block',
                marginTop:    8,
                padding:      '4px 12px',
                fontFamily:   'var(--font-display)',
                fontSize:     12,
                fontWeight:   600,
                color:        'var(--error)',
                background:   'transparent',
                border:       '1px solid var(--error)',
                borderRadius: 12,
                cursor:       'pointer',
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {isSettingsMode && mode && (
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!submittableInSettingsMode || submitting}
          data-testid="github-setup-submit"
          style={{
            marginTop:    12,
            padding:      '8px 16px',
            fontFamily:   'var(--font-display)',
            fontSize:     13,
            fontWeight:   600,
            color:        'white',
            background:   'var(--primary)',
            border:       'none',
            borderRadius: 'var(--radius-sm)',
            cursor:       (!submittableInSettingsMode || submitting) ? 'not-allowed' : 'pointer',
            opacity:      (!submittableInSettingsMode || submitting) ? 0.5 : 1,
          }}
        >
          {submitting ? 'Configuring…' : (mode === 'skip' ? 'Confirm skip' : 'Configure GitHub')}
        </button>
      )}
    </div>
  );
}
